import React, { useState, useEffect } from 'react';
import openaiService from '../services/openaiService.js';
import { useTranslation } from 'react-i18next';
import '../styles/AIAnalysisPanel.css';
import '../styles/InlineErrors.css';

const DemoAIAnalysisPanel = ({ content, onAnalysisComplete, onInlineErrorsUpdate, isImageAnalysis = false }) => {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(true);
  const [apiKeyValid, setApiKeyValid] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [showInlineErrors, setShowInlineErrors] = useState(true);

  // Load saved API key from localStorage on component mount
  useEffect(() => {
    const savedApiKey = localStorage.getItem('openai_api_key');
    
    // Always disable mock mode for demo
    openaiService.setMockMode(false);
    
    if (savedApiKey) {
      setApiKey(savedApiKey);
      setShowApiKeyInput(false);
      openaiService.setApiKey(savedApiKey);
      validateApiKey(savedApiKey);
    }
  }, []);

  // Validate API key
  const validateApiKey = async (key) => {
    if (!openaiService.isValidApiKey(key)) {
      setApiKeyValid(false);
      return;
    }

    try {
      const isValid = await openaiService.testApiKey(key);
      setApiKeyValid(isValid);
      if (!isValid) {
        setError(t('aiAnalysis.errors.invalidApiKey'));
      }
    } catch (err) {
      setApiKeyValid(false);
      setError(t('aiAnalysis.errors.apiKeyValidationFailed'));
    }
  };

  // Validate API key and return result directly (for form submission)
  const validateApiKeyAndReturn = async (key) => {
    if (!openaiService.isValidApiKey(key)) {
      setError('Invalid API key format');
      return false;
    }

    try {
      const isValid = await openaiService.testApiKey(key);
      if (!isValid) {
        setError(t('aiAnalysis.errors.invalidApiKey'));
        return false;
      }
      setError(null);
      return true;
    } catch (err) {
      setError(t('aiAnalysis.errors.apiKeyValidationFailed'));
      return false;
    }
  };

  // Handle API key submission
  const handleApiKeySubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!apiKey.trim()) {
      setError(t('aiAnalysis.errors.apiKeyRequired'));
      return;
    }

    // Save API key to localStorage and service
    localStorage.setItem('openai_api_key', apiKey);
    openaiService.setApiKey(apiKey);
    
    // Validate API key and get the result directly
    const isValid = await validateApiKeyAndReturn(apiKey);
    
    if (isValid) {
      setApiKeyValid(true);
      setShowApiKeyInput(false);
    }
  };

  // Handle essay analysis
  const handleAnalyze = async () => {
    if (!content) {
      setError('No content provided for analysis');
      return;
    }

    // For image analysis, we don't need to check text length
    if (!isImageAnalysis && content.trim().length < 50) {
      setError(t('aiAnalysis.errors.contentTooShort'));
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setProgress('');
    setAnalysis(null);

    try {
      let result;
      
      if (isImageAnalysis) {
        // For image analysis, pass the image data directly
        result = await openaiService.analyzeEssay('', setProgress, content);
      } else {
        // Extract plain text from HTML content for text analysis
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';

        if (plainText.trim().length < 50) {
          throw new Error(t('aiAnalysis.errors.contentTooShort'));
        }

        result = await openaiService.analyzeEssay(plainText, setProgress);
      }

      const parsedAnalysis = openaiService.parseAnalysisResponse(result.analysis);
      
      const analysisData = {
        ...parsedAnalysis,
        rawAnalysis: result.analysis,
        usage: openaiService.getUsageStats(result.usage),
        timestamp: result.timestamp,
        isImageAnalysis: result.isImageAnalysis
      };

      setAnalysis(analysisData);

      // Notify parent component about inline errors (only for text analysis)
      if (onInlineErrorsUpdate && parsedAnalysis.inlineErrors && !isImageAnalysis) {
        onInlineErrorsUpdate(parsedAnalysis.inlineErrors);
      }

      if (onAnalysisComplete) {
        onAnalysisComplete(parsedAnalysis);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setIsAnalyzing(false);
      setProgress('');
    }
  };

  // Clear API key
  const handleClearApiKey = () => {
    localStorage.removeItem('openai_api_key');
    setApiKey('');
    setShowApiKeyInput(true);
    setApiKeyValid(false);
    openaiService.setApiKey(null);
  };

  // Get error type label
  const getErrorTypeLabel = (type) => {
    const labels = {
      grammar: 'Grammar',
      spelling: 'Spelling',
      punctuation: 'Punctuation',
      agreement: 'Agreement',
      structure: 'Structure',
      argument: 'Argument',
      style: 'Style'
    };
    return labels[type] || type;
  };

  // Get error icon
  const getErrorIcon = (type) => {
    const icons = {
      grammar: '⚠️',
      spelling: '📝',
      punctuation: '❗',
      agreement: '🔄',
      structure: '🏗️',
      argument: '💭',
      style: '🎨'
    };
    return icons[type] || '❓';
  };

  // Render competency scores
  const renderCompetencyScores = () => {
    if (!analysis?.scores) return null;

    const competencies = ['I', 'II', 'III', 'IV', 'V'];
    
    return (
      <div className="competency-scores">
        <h4>{t('aiAnalysis.competencyScores')}</h4>
        {competencies.map(comp => {
          const score = analysis.scores[comp];
          if (!score) return null;
          
          const percentage = (score.score / 200) * 100;
          const colorClass = percentage >= 80 ? 'excellent' : 
                           percentage >= 60 ? 'good' : 
                           percentage >= 40 ? 'average' : 'poor';
          
          return (
            <div key={comp} className="competency-item">
              <div className="competency-header">
                <span className="competency-name">
                  {t(`aiAnalysis.competencies.${comp}`)}
                </span>
                <span className={`competency-score ${colorClass}`}>
                  {score.score}/200
                </span>
              </div>
              <div className="competency-bar">
                <div 
                  className={`competency-fill ${colorClass}`}
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <p className="competency-justification">{score.justification}</p>
            </div>
          );
        })}
        
        {analysis.totalScore > 0 && (
          <div className="total-score">
            <strong>
              {t('aiAnalysis.totalScore')}: {analysis.totalScore}/1000
            </strong>
          </div>
        )}
      </div>
    );
  };

  const renderGuidedSections = () => {
    if (!analysis) return null;

    const strengths = Array.isArray(analysis.strengths)
      ? analysis.strengths.filter((item) => typeof item === 'string' && item.trim())
      : [];
    const exemplarSentences = Array.isArray(analysis.exemplarSentences)
      ? analysis.exemplarSentences
          .map((item) => ({
            excerpt: typeof item?.excerpt === 'string' ? item.excerpt.trim() : '',
            why: typeof item?.whyItWorks === 'string' ? item.whyItWorks.trim() : ''
          }))
          .filter((item) => item.excerpt && item.why)
      : [];
    const repertorio = Array.isArray(analysis.repertorioSociocultural)
      ? analysis.repertorioSociocultural
          .map((item) => ({
            reference: typeof item?.reference === 'string' ? item.reference.trim() : '',
            application: typeof item?.application === 'string' ? item.application.trim() : ''
          }))
          .filter((item) => item.reference && item.application)
      : [];
    const roadmap = Array.isArray(analysis.roadmapTo1000)
      ? analysis.roadmapTo1000.filter((item) => typeof item === 'string' && item.trim())
      : [];

    if (!strengths.length && !exemplarSentences.length && !repertorio.length && !roadmap.length) {
      return null;
    }

    return (
      <div className="guided-sections">
        {strengths.length > 0 && (
          <div className="feedback-section strengths-section">
            <h4>Key strengths</h4>
            <ul className="strengths-list">
              {strengths.map((item, index) => (
                <li key={`strength-${index}`}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {exemplarSentences.length > 0 && (
          <div className="feedback-section exemplar-section">
            <h4>Standout sentences to keep</h4>
            <div className="exemplar-grid">
              {exemplarSentences.map((item, index) => (
                <article key={`exemplar-${index}`} className="exemplar-card">
                  <blockquote>“{item.excerpt}”</blockquote>
                  <p className="exemplar-reason">{item.why}</p>
                </article>
              ))}
            </div>
          </div>
        )}

        {repertorio.length > 0 && (
          <div className="feedback-section repertorio-section">
            <h4>Suggested repertoire</h4>
            <div className="repertorio-grid">
              {repertorio.map((item, index) => (
                <div key={`rep-${index}`} className="repertorio-card">
                  <strong>{item.reference}</strong>
                  <p>{item.application}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {roadmap.length > 0 && (
          <div className="feedback-section roadmap-section">
            <h4>Roadmap to reach 1000</h4>
            <ol className="roadmap-list">
              {roadmap.map((item, index) => (
                <li key={`roadmap-${index}`}>
                  <span className="step-index">Step {index + 1}</span>
                  <span className="step-text">{item}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    );
  };

  // Render inline errors section
  const renderInlineErrors = () => {
    if (!analysis?.inlineErrors || analysis.inlineErrors.length === 0) return null;

    return (
      <div className="inline-errors-section">
        <div className="section-header">
          <h4>Inline Markups ({analysis.inlineErrors.length})</h4>
          <button
            onClick={() => setShowInlineErrors(!showInlineErrors)}
            className="toggle-section"
          >
            {showInlineErrors ? 'Hide' : 'Show'}
          </button>
        </div>
        
        {showInlineErrors && (
          <div className="inline-errors-list">
            {analysis.inlineErrors.map((error, index) => (
              <div key={index} className={`inline-error-item error-type-${error.type}`}>
                <div className="error-header">
                  <span className="error-type">{getErrorTypeLabel(error.type)}</span>
                  <span className="error-icon">{getErrorIcon(error.type)}</span>
                </div>
                <div className="error-content">
                  <div className="error-original">
                    <strong>Original:</strong> "{error.originalText}"
                  </div>
                  <div className="error-correction">
                    <strong>Suggested:</strong> "{error.correction}"
                  </div>
                  <div className="error-explanation">
                    {error.explanation}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render problems and suggestions
  const renderProblemsAndSuggestions = () => {
    if (!analysis) return null;

    return (
      <div className="problems-suggestions">
        {analysis.problems && analysis.problems.length > 0 && (
          <div className="problems-section">
            <h4>{t('aiAnalysis.problemsIdentified')}</h4>
            <ul className="problems-list">
              {analysis.problems.map((problem, index) => (
                <li key={index} className="problem-item">
                  {problem}
                </li>
              ))}
            </ul>
          </div>
        )}

        {analysis.suggestions && analysis.suggestions.length > 0 && (
          <div className="suggestions-section">
            <h4>{t('aiAnalysis.suggestions')}</h4>
            <ul className="suggestions-list">
              {analysis.suggestions.map((suggestion, index) => (
                <li key={index} className="suggestion-item">
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}

        {analysis.correctedVersion && (
          <div className="corrected-version">
            <h4>{t('aiAnalysis.correctedVersion')}</h4>
            <div className="corrected-text">
              {analysis.correctedVersion}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ai-analysis-panel">
      <div className="ai-panel-header">
        <h3>{t('aiAnalysis.title')}</h3>
        <p className="ai-panel-description">
          {t('aiAnalysis.description')}
        </p>
        
        {/* Demo Mode Notice - No Mock Mode Toggle */}
  
      </div>

      {showApiKeyInput ? (
        <div className="api-key-section">
          <h4>{t('aiAnalysis.apiKeySetup')}</h4>
          <p className="api-key-info">
            {t('aiAnalysis.apiKeyInfo')}
          </p>
          <form onSubmit={handleApiKeySubmit} className="api-key-form">
            <div className="api-key-input-group">
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={t('aiAnalysis.apiKeyPlaceholder')}
                className="api-key-input"
              />
              <button 
                type="submit" 
                className="api-key-submit"
                disabled={!apiKey.trim()}
              >
                {t('aiAnalysis.saveApiKey')}
              </button>
            </div>
          </form>
          <div className="api-key-help">
            <p>{t('aiAnalysis.apiKeyHelp')}</p>
            <a 
              href="https://platform.openai.com/api-keys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="api-key-link"
            >
              {t('aiAnalysis.getApiKey')}
            </a>
          </div>
        </div>
      ) : (
        <div className="api-key-configured">
          <div className="api-key-status">
            <span className={`api-status ${apiKeyValid ? 'valid' : 'invalid'}`}>
              {apiKeyValid ? t('aiAnalysis.apiKeyValid') : t('aiAnalysis.apiKeyInvalid')}
            </span>
            <button 
              onClick={handleClearApiKey}
              className="change-api-key"
            >
              {t('aiAnalysis.changeApiKey')}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {!showApiKeyInput && apiKeyValid && (
        <div className="analysis-section">
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing || !content}
            className="analyze-button"
          >
            {isAnalyzing ? t('aiAnalysis.analyzing') : t('aiAnalysis.analyzeButton')}
          </button>

          {progress && (
            <div className="progress-message">
              {progress}
            </div>
          )}

          {analysis && (
              <div className="analysis-results">
                <div className="analysis-header">
                  <h4>{t('aiAnalysis.analysisResults')}</h4>
                  <button
                    onClick={() => setShowFullAnalysis(!showFullAnalysis)}
                    className="toggle-analysis"
                  >
                    {showFullAnalysis ? t('aiAnalysis.hideDetails') : t('aiAnalysis.showDetails')}
                  </button>
                </div>

                {renderCompetencyScores()}
                {renderGuidedSections()}
                {renderInlineErrors()}
                {renderProblemsAndSuggestions()}

              {showFullAnalysis && (
                <div className="full-analysis">
                  <h4>{t('aiAnalysis.fullAnalysis')}</h4>
                  <div className="analysis-text">
                    {analysis.rawAnalysis}
                  </div>
                </div>
              )}

              {analysis.usage && (
                <div className="usage-stats">
                  <small>
                    {t('aiAnalysis.tokensUsed')}: {analysis.usage.totalTokens} | 
                    {t('aiAnalysis.estimatedCost')}: ${analysis.usage.estimatedCost.toFixed(4)}
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DemoAIAnalysisPanel;
